import * as CustomerStatisticsActions from '../../../../actions/customer-statistics';
import { ArticleEventCard } from '../articleEventCard';
import { CircularProgress, Grid, TextField } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { NotificationFilterType } from './notificationFilter';
import { RootState } from '../../../../reducers';
import { StockArticleEvent } from '../../../../model';
import { WikusButton } from '@wikus-ui/components/core/button';
import { WikusConfirmation } from '@wikus-ui/components/core/confirmation';
import { unwrapCustomerState } from '../../../../utils/url';
import { useActions } from '../../../../actions';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const ORDER_EVENT_NAMES = [
  'OrderMailSentEvent',
  'OrderMailToCustomerSentEvent',
  'OrderMailCustomArticleSentEvent'
];

const SHIPMENT_EVENT_NAMES = [
  'ArticlesShippedEvent'
];

const isOrderEvent = (event: StockArticleEvent): boolean => {
  return ORDER_EVENT_NAMES.includes(event.eventName);
};

export const isShipmentEvent = (event: StockArticleEvent): boolean => {
  return SHIPMENT_EVENT_NAMES.includes(event.eventName);
};

const isCustomOrderEvent = (event: StockArticleEvent): boolean => {
  return event.eventName === 'OrderMailCustomArticleSentEvent';
};

export const CustomerStatisticsNotifications = ({
  customerId,
  articleIds,
  filter,
}: {
  customerId: string;
  articleIds?: number[];
  filter: NotificationFilterType;
}) => {
  const intl = useIntl();
  const statistics = useSelector((state: RootState) =>
    unwrapCustomerState(state.customerStatistics)
  );
  const customerStatisticsActions: typeof CustomerStatisticsActions =
    useActions(CustomerStatisticsActions);
  const [confirmOrder, setConfirmOrder] = useState<StockArticleEvent>();
  const [confirmCustomArrival, setConfirmCustomArrival] = useState<StockArticleEvent>();
  const [arrivalAmount, setArrivalAmount] = useState<number>(0);

  // Fetch data based on filter
  useEffect(() => {
    if (filter === 'all') {
      // Fetch both orders and shipments
      customerStatisticsActions.fetchStatisticsOrders({ customerId, articleIds });
      customerStatisticsActions.fetchStatisticsShipments({ customerId, articleIds });
    } else if (filter === 'orders') {
      customerStatisticsActions.fetchStatisticsOrders({ customerId, articleIds });
    } else if (filter === 'shipments') {
      customerStatisticsActions.fetchStatisticsShipments({ customerId, articleIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, articleIds, customerId]);

  // Combine, filter, and sort notifications
  const notifications = useMemo(() => {
    let data: StockArticleEvent[] = [];

    if (filter === 'all') {
      // Combine both orders and shipments
      data = [...(statistics?.orders || []), ...(statistics?.shipments || [])];
    } else if (filter === 'orders') {
      data = statistics?.orders || [];
    } else if (filter === 'shipments') {
      data = statistics?.shipments || [];
    }
    // Filter out hidden notifications and ArticleItemShippedEvent
    // (like Flutter does - only show ArticlesShippedEvent for full shipment confirmation)
    const filtered = data.filter((notification) => {
      if (notification.hidden) return false;
      // Filter out ArticleItemShippedEvent - only show ArticlesShippedEvent
      if (notification.eventName === 'ArticleItemShippedEvent') return false;
      return true;
    });

    // Sort by createdAt descending (newest first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [statistics?.orders, statistics?.shipments, filter]);

  const isLoading = useMemo(() => {
    if (filter === 'all') {
      return !statistics?.orders || !statistics?.shipments;
    } else if (filter === 'orders') {
      return !statistics?.orders;
    } else if (filter === 'shipments') {
      return !statistics?.shipments;
    }
    return false;
  }, [filter, statistics?.orders, statistics?.shipments]);

  const onFulfill = async (order: StockArticleEvent) => {
    const resp = await customerStatisticsActions.fulfillStatisticsOrder({
      customerId,
      orderId: order.id,
    });
    setConfirmOrder(undefined);
    return resp;
  };

  const onConfirmShipment = async (shipment: StockArticleEvent) => {
    const resp = await customerStatisticsActions.confirmStatisticsShipment({
      customerId,
      shipmentId: shipment.id,
    });
    // Re-fetch shipments to update the list
    customerStatisticsActions.fetchStatisticsShipments({ customerId, articleIds });
    return resp;
  };

  const onConfirmCustomArrival = async (event: StockArticleEvent) => {
    const resp = await customerStatisticsActions.confirmCustomArticleArrival({
      customerId,
      eventId: event.id,
      amount: arrivalAmount,
    });
    setConfirmCustomArrival(undefined);
    setArrivalAmount(0);
    // Re-fetch orders to update the list
    customerStatisticsActions.fetchStatisticsOrders({ customerId, articleIds });
    return resp;
  };

  return (
    <>
      <Grid container spacing={2}>
        {isLoading ? (
          <Grid container justifyContent="center">
            <CircularProgress style={{ width: '25px', height: '25px' }} color="inherit" />
          </Grid>
        ) : null}
        {notifications.length === 0 && !isLoading ? (
          <Grid container justifyContent="center">
            <FormattedMessage id="nothing-found" />
          </Grid>
        ) : undefined}
        {notifications.map((notification) => (
          <Grid item xs key={notification.id}>
            <ArticleEventCard event={notification} showArticleId>
              {isOrderEvent(notification) && (
                <WikusButton
                  onClick={() => setConfirmOrder(notification)}
                  variant="outlined"
                  color="primary"
                  style={{ marginRight: 8, marginBottom: 8 }}
                >
                  {intl.formatMessage({
                    id: 'customers.orders.fulfill',
                  })}
                </WikusButton>
              )}
              {isCustomOrderEvent(notification) && (
                <WikusButton
                  onClick={() => {
                    setArrivalAmount(notification.amount || 0);
                    setConfirmCustomArrival(notification);
                  }}
                  variant="outlined"
                  color="primary"
                  style={{ marginRight: 8, marginBottom: 8 }}
                >
                  {intl.formatMessage({
                    id: 'customers.orders.confirmArrival',
                  })}
                </WikusButton>
              )}
              {isShipmentEvent(notification) && (
                <WikusButton
                  onClick={() => onConfirmShipment(notification)}
                  variant="outlined"
                  color="primary"
                  style={{ marginBottom: 8 }}
                >
                  {intl.formatMessage({
                    id: 'customers.shipments.confirm',
                  })}
                </WikusButton>
              )}
            </ArticleEventCard>
          </Grid>
        ))}
      </Grid>
      <WikusConfirmation
        open={!!confirmOrder}
        title={intl.formatMessage({
          id: 'customers.orders.fulfill.title',
        })}
        onConfirm={() => onFulfill(confirmOrder!)}
        onAbort={() => setConfirmOrder(undefined)}
        severity="warning"
      >
        <FormattedMessage id="customers.orders.fulfill.message" />
      </WikusConfirmation>
      <WikusConfirmation
        open={!!confirmCustomArrival}
        title={intl.formatMessage({
          id: 'customers.orders.confirmArrival.title',
        })}
        onConfirm={() => onConfirmCustomArrival(confirmCustomArrival!)}
        onAbort={() => {
          setConfirmCustomArrival(undefined);
          setArrivalAmount(0);
        }}
        severity="warning"
      >
        <Grid container spacing={2} direction="column">
          <Grid item>
            <FormattedMessage id="customers.orders.confirmArrival.message" />
          </Grid>
          <Grid item>
            <TextField
              label={intl.formatMessage({ id: 'customers.orders.confirmArrival.amount' })}
              type="number"
              value={arrivalAmount}
              onChange={(e) => setArrivalAmount(Number(e.target.value))}
              fullWidth
              variant="outlined"
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
        </Grid>
      </WikusConfirmation>
    </>
  );
};
